import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Col, Row, Card, CardBody, Badge, Button, Modal, ModalHeader, ModalBody,
  ModalFooter, Spinner, Input, Alert, Nav, NavItem, NavLink,
  FormGroup, Label
} from "reactstrap";
import classnames from "classnames";
import TableContainer from "../../../components/Common/TableContainer";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import { getCarriers, createShipment as seCreateShipment, getShipmentLabel } from "../../../services/shipEntegraService";
import {
  getCargoIntegrations,
  getWarehouses,
  createShipment as keCreateShipment,
  printLabel,
} from "../../../services/kargoEntegratorService";
import { API_BASE, getAuthHeaders } from "../../../helpers/apiConfig";
import { downloadObjectsCsv } from "../../../helpers/csvExport";

const BASE_URL = API_BASE;

// DB'deki kanonik durum değeri "OdemeBekliyor" (boşluksuz) — CheckoutScreen ön-siparişi,
// PaymentController ve mezat kabul akışı hep bu değeri yazar. Ekranda okunabilir
// etiketiyle gösteriyoruz (DURUM_ETIKET), filtreleme ham değerle yapılır.
const DURUM_ETIKET = { "OdemeBekliyor": "Ödeme Bekliyor" };
const durumLabel = (d) => DURUM_ETIKET[d] || d || "Bilinmiyor";

const DURUM_RENKLERI = {
  "OdemeBekliyor": "dark",
  "Beklemede": "warning",
  "Onaylandı": "info",
  "Kargoya Verildi": "primary",
  "Teslim Edildi": "success",
  "İptal Edildi": "danger",
  "İade Edildi": "secondary",
};

const SiparisYonetimi = () => {
  document.title = "Sipariş Yönetimi | HarmanaGEL";

  const [siparisler, setSiparisler] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState("");
  const [activeTab, setActiveTab] = useState("tumu");

  const [detailModal, setDetailModal] = useState(false);
  const [selectedSiparis, setSelectedSiparis] = useState(null);
  const [newStatus, setNewStatus] = useState("");

  // Kargo oluştur modal
  const [shipModal, setShipModal] = useState(false);
  const [shipProvider, setShipProvider] = useState("ke"); // "ke" | "se"
  const [shipLoading, setShipLoading] = useState(false);
  const [addressLoading, setAddressLoading] = useState(false);
  const [lastCreatedShipment, setLastCreatedShipment] = useState(null); // {provider, id, barcode}

  // ShipEntegra
  const [carriers, setCarriers] = useState([]);
  const [carriersLoading, setCarriersLoading] = useState(false);
  const [shipForm, setShipForm] = useState({
    carrierId: "", receiverName: "", receiverPhone: "",
    receiverAddress: "", receiverCity: "", receiverDistrict: "",
    receiverPostalCode: "", weight: "1", desi: "1",
    description: "", paymentType: "0",
  });

  // KargoEntegratör
  const [keIntegrations, setKeIntegrations] = useState([]);
  const [keWarehouses, setKeWarehouses] = useState([]);
  const [keLoading, setKeLoading] = useState(false);
  const [keError, setKeError] = useState(null);
  const [keForm, setKeForm] = useState({
    cargo_integration_id: "", warehouse_id: "",
    name: "", surname: "", phone: "", email: "",
    country: "TÜRKİYE", postcode: "", city: "", district: "", address: "",
    payment_type: "cash_money", package_type: "box", payor_type: "sender",
    is_pay_at_door: false, desi: "1", kg: "1", note: "",
  });

  const fetchCarriers = useCallback(async () => {
    if (carriers.length > 0) return;
    setCarriersLoading(true);
    try {
      const list = await getCarriers();
      setCarriers(list);
    } catch {
      setError("Kargo firmaları yüklenemedi.");
    } finally {
      setCarriersLoading(false);
    }
  }, [carriers.length]);

  const fetchKEData = useCallback(async () => {
    if (keIntegrations.length > 0 && keWarehouses.length > 0) return;
    setKeLoading(true);
    setKeError(null);
    try {
      const [integrations, warehouses] = await Promise.all([
        getCargoIntegrations(),
        getWarehouses(),
      ]);
      setKeIntegrations(integrations);
      setKeWarehouses(warehouses);
    } catch (err) {
      setKeError(err.message);
    } finally {
      setKeLoading(false);
    }
  }, [keIntegrations.length, keWarehouses.length]);

  const openShipModal = async (siparis) => {
    setSelectedSiparis(siparis);
    const u = siparis.KULLANICILAR;
    const adSoyad = u?.AD_SOYAD || "";
    const adParcalari = adSoyad.split(" ");
    const ad = adParcalari.slice(0, -1).join(" ") || adSoyad;
    const soyad = adParcalari.length > 1 ? adParcalari[adParcalari.length - 1] : "";

    setShipProvider("ke");
    setLastCreatedShipment(null);
    setShipForm({
      carrierId: "", receiverName: adSoyad, receiverPhone: u?.CEP_NO || "",
      receiverAddress: "", receiverCity: "", receiverDistrict: "",
      receiverPostalCode: "", weight: "1", desi: "1",
      description: `Sipariş #${siparis.ID}`, paymentType: "0",
    });
    setKeForm({
      cargo_integration_id: "", warehouse_id: "",
      name: ad, surname: soyad, phone: u?.CEP_NO || "", email: "",
      country: "TÜRKİYE", postcode: "", city: "", district: "", address: "",
      payment_type: "cash_money", package_type: "box", payor_type: "sender",
      is_pay_at_door: false, desi: "1", kg: "1", note: `Sipariş #${siparis.ID}`,
    });
    setShipModal(true);
    fetchKEData();

    // Teslimat adresi otomatik doldur (TESLIMAT_ADRES_ID üzerinden)
    if (siparis.TESLIMAT_ADRES_ID) {
      setAddressLoading(true);
      try {
        const res = await fetch(
          `${BASE_URL}/KULLANICI_ADRESLERI?$filter=ID eq ${siparis.TESLIMAT_ADRES_ID}`,
          { headers: getAuthHeaders() }
        );
        if (res.ok) {
          const data = await res.json();
          const adres = data.value?.[0] || data;
          const il = adres.IL || "";
          const ilce = adres.ILCE || "";
          const acikAdres = adres.ACIK_ADRES || "";
          setKeForm(prev => ({ ...prev, city: il, district: ilce, address: acikAdres }));
          setShipForm(prev => ({ ...prev, receiverCity: il, receiverDistrict: ilce, receiverAddress: acikAdres }));
        }
      } catch {
        // adres yüklenemedi, kullanıcı manuel girer
      } finally {
        setAddressLoading(false);
      }
    }
  };

  const handleProviderChange = (p) => {
    setShipProvider(p);
    if (p === "se") fetchCarriers();
    if (p === "ke") fetchKEData();
  };

  const handlePrintLabel = async () => {
    if (!lastCreatedShipment?.id) return;
    try {
      if (lastCreatedShipment.provider === "ke") {
        const blobUrl = await printLabel(lastCreatedShipment.id);
        window.open(blobUrl, "_blank");
      } else {
        const data = await getShipmentLabel(lastCreatedShipment.id);
        const url = data?.url || data?.label_url || data?.labelUrl || data;
        if (url) window.open(url, "_blank");
      }
    } catch (err) {
      setError("Etiket alınamadı: " + err.message);
    }
  };

  const handleCreateShipment = async () => {
    setShipLoading(true);
    try {
      let kargoNo = "";
      let kargoFirmasi = "";
      let referansId = null;
      let takipNo = "";
      let takipLinki = "";

      if (shipProvider === "ke") {
        if (!keForm.cargo_integration_id) { setError("Lütfen kargo firması seçin."); setShipLoading(false); return; }
        if (!keForm.warehouse_id) { setError("Lütfen depo seçin."); setShipLoading(false); return; }

        const payload = {
          cargo_integration_id: parseInt(keForm.cargo_integration_id, 10),
          warehouse_id: parseInt(keForm.warehouse_id, 10),
          customer: {
            name: keForm.name, surname: keForm.surname, phone: keForm.phone,
            email: keForm.email, country: keForm.country || "TÜRKİYE",
            postcode: keForm.postcode, city: keForm.city,
            district: keForm.district, address: keForm.address,
          },
          payment_type: keForm.payment_type,
          package_type: keForm.package_type,
          payor_type: keForm.payor_type,
          is_pay_at_door: keForm.is_pay_at_door,
          desi: parseFloat(keForm.desi) || 1,
          kg: parseFloat(keForm.kg) || 1,
          note: keForm.note,
          platform_id: selectedSiparis.ID,
          platform_d_id: selectedSiparis.ID,
        };
        const result = await keCreateShipment(payload);
        kargoNo = result.barcode || "";
        takipNo = result.tracking_number || result.barcode || "";
        takipLinki = result.tracking_link || "";
        referansId = result.id || null;
        kargoFirmasi = result.cargo_company?.title || "";
        setLastCreatedShipment({ provider: "ke", id: result.id, barcode: kargoNo || takipNo });
        setSuccessMsg(`Kargo oluşturuldu! Barkod: ${kargoNo || takipNo || "-"}`);
      } else {
        if (!shipForm.carrierId) { setError("Lütfen kargo firması seçin."); setShipLoading(false); return; }

        const payload = {
          carrierId: shipForm.carrierId,
          receiver: {
            name: shipForm.receiverName, phone: shipForm.receiverPhone,
            address: shipForm.receiverAddress, city: shipForm.receiverCity,
            district: shipForm.receiverDistrict, postalCode: shipForm.receiverPostalCode,
          },
          package: { weight: parseFloat(shipForm.weight) || 1, desi: parseFloat(shipForm.desi) || 1 },
          description: shipForm.description,
          paymentType: parseInt(shipForm.paymentType, 10),
          referenceNo: `SIPARIS-${selectedSiparis.ID}`,
        };
        const result = await seCreateShipment(payload);
        kargoNo = result.barcode || result.trackingNumber || "";
        takipNo = result.trackingNumber || result.barcode || "";
        takipLinki = result.trackingLink || "";
        referansId = result.id || null;
        kargoFirmasi = result.carrierName || "";
        setLastCreatedShipment({ provider: "se", id: result.id || result.shipmentId, barcode: kargoNo });
        setSuccessMsg(`Kargo oluşturuldu! Takip No: ${kargoNo || "-"}`);
      }

      // SIPARIS tablosunu güncelle
      await fetch(`${BASE_URL}/SIPARIS(${selectedSiparis.ID})`, {
        method: "PATCH",
        headers: getAuthHeaders(),
        body: JSON.stringify({
          SIPARIS_DURUMU: "Kargoya Verildi",
          KARGO_NO: kargoNo || takipNo,
          KARGO_FIRMASI: kargoFirmasi,
        }),
      });

      // SIPARIS_TESLIIMATLARI kaydı oluştur
      await fetch(`${BASE_URL}/SIPARIS_TESLIIMATLARI`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({
          SIPARIS_ID: selectedSiparis.ID,
          KARGO_FIRMasi: kargoFirmasi,
          KARGO_TAKIP_NO: takipNo,
          KARGO_REFERANS_ID: referansId ? String(referansId) : null,
          KARGO_TAKIP_LINKI: takipLinki,
          TESLIMA_DURUMU: "non_processed",
        }),
      });

      setShipModal(false);
      fetchSiparisler();
      setTimeout(() => setSuccessMsg(""), 4000);
    } catch (err) {
      setError("Kargo oluşturma hatası: " + err.message);
    } finally {
      setShipLoading(false);
    }
  };

  const fetchSiparisler = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${BASE_URL}/SIPARIS?$top=2000&$orderby=SIPARIS_TARIHI desc&$expand=KULLANICILAR($select=AD_SOYAD,Mail_Adress,CEP_NO),SIPARIS_DETAY`,
        { headers: getAuthHeaders() }
      );
      const result = await response.json();
      if (result && result.value) setSiparisler(result.value);
    } catch (err) {
      setError("Siparişler yüklenirken hata oluştu.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSiparisler();
  }, []);

  const filteredSiparisler = useMemo(() => {
    if (activeTab === "tumu") return siparisler;
    return siparisler.filter(s => s.SIPARIS_DURUMU === activeTab);
  }, [siparisler, activeTab]);

  const handleUpdateStatus = async () => {
    if (!newStatus || !selectedSiparis) return;
    try {
      await fetch(`${BASE_URL}/SIPARIS(${selectedSiparis.ID})`, {
        method: "PATCH",
        headers: getAuthHeaders(),
        body: JSON.stringify({ SIPARIS_DURUMU: newStatus }),
      });
      setSuccessMsg("Sipariş durumu güncellendi.");
      setDetailModal(false);
      fetchSiparisler();
      setTimeout(() => setSuccessMsg(""), 3000);
    } catch (err) {
      setError("Güncelleme sırasında hata oluştu.");
    }
  };

  // Mezat siparişi ödeme onayı: satıcıya net tutar aktarılır, sipariş "Onaylandı" olur.
  // Backend: Mezat/OdemeOnayla (admin, idempotent — yalnızca "Ödeme Bekliyor").
  const handleOdemeOnayla = async (s) => {
    const net = parseFloat(s.NET_TOPLAM || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2 });
    if (!window.confirm(`#${s.ID} numaralı mezat siparişinin ödemesini onaylıyor musunuz?\nSatıcıya net tutar (${net} ₺) cüzdanına aktarılacak ve sipariş kargo hattına girecek.`)) return;
    try {
      const res = await fetch(`${BASE_URL}/Mezat/OdemeOnayla`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({ SiparisId: s.ID }),
      });
      if (!res.ok) {
        const t = await res.text().catch(() => "");
        setError(t ? t.slice(0, 180) : "Ödeme onayı başarısız.");
        return;
      }
      setSuccessMsg("Ödeme onaylandı, satıcı payı aktarıldı.");
      fetchSiparisler();
      setTimeout(() => setSuccessMsg(""), 3000);
    } catch (err) {
      setError("Ödeme onayı sırasında hata oluştu.");
    }
  };

  const statusCounts = useMemo(() => {
    const counts = {};
    siparisler.forEach(s => {
      counts[s.SIPARIS_DURUMU] = (counts[s.SIPARIS_DURUMU] || 0) + 1;
    });
    return counts;
  }, [siparisler]);

  const columns = useMemo(() => [
    {
      header: "Sipariş No",
      accessorKey: "ID",
      enableSorting: true,
      enableColumnFilter: false,
      cell: (cell) => (
        <div>
          <span className="fw-bold text-primary">#{cell.getValue()}</span>
          {cell.row.original.MEZAT_ID ? (
            <Badge color="warning" className="ms-1" title={`Mezat #${cell.row.original.MEZAT_ID}`}>
              <i className="bx bx-gavel me-1" />Mezat
            </Badge>
          ) : null}
        </div>
      ),
    },
    {
      header: "Müşteri",
      accessorKey: "KULLANICILAR",
      enableSorting: false,
      enableColumnFilter: false,
      cell: (cell) => {
        const u = cell.getValue();
        return u ? (
          <div>
            <div className="fw-medium">{u.AD_SOYAD}</div>
            <small className="text-muted">{u.Mail_Adress}</small>
          </div>
        ) : <span className="text-muted">ID: {cell.row.original.KULLANICI_ID}</span>;
      },
    },
    {
      header: "Tutar",
      accessorKey: "TOPLAM_TUTAR",
      enableSorting: true,
      enableColumnFilter: false,
      cell: (cell) => (
        <span className="fw-bold text-success">
          {parseFloat(cell.getValue() || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2 })} ₺
        </span>
      ),
    },
    {
      header: "Durum",
      accessorKey: "SIPARIS_DURUMU",
      enableSorting: true,
      enableColumnFilter: false,
      cell: (cell) => (
        <Badge color={DURUM_RENKLERI[cell.getValue()] || "secondary"}>
          {durumLabel(cell.getValue())}
        </Badge>
      ),
    },
    {
      header: "Sipariş Tarihi",
      accessorKey: "SIPARIS_TARIHI",
      enableSorting: true,
      enableColumnFilter: false,
      cell: (cell) => {
        const d = cell.getValue();
        return d ? new Date(d).toLocaleDateString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "-";
      },
    },
    {
      header: "Ürün Adedi",
      accessorKey: "SIPARIS_DETAY",
      enableSorting: false,
      enableColumnFilter: false,
      cell: (cell) => {
        const detay = cell.getValue();
        return <Badge color="light" className="text-dark">{Array.isArray(detay) ? detay.length : 0} ürün</Badge>;
      },
    },
    {
      header: "İşlemler",
      enableSorting: false,
      enableColumnFilter: false,
      cell: (cellProps) => {
        const s = cellProps.row.original;
        return (
          <div className="d-flex gap-1">
            <Button color="primary" size="sm" outline onClick={() => {
              setSelectedSiparis(s);
              setNewStatus(s.SIPARIS_DURUMU);
              setDetailModal(true);
            }}>
              <i className="bx bx-edit me-1" />Detay
            </Button>
            {s.SIPARIS_DURUMU === "OdemeBekliyor" && s.MEZAT_ID && (
              <Button color="dark" size="sm" onClick={() => handleOdemeOnayla(s)}>
                <i className="bx bx-check-shield me-1" />Ödemeyi Onayla
              </Button>
            )}
            {(s.SIPARIS_DURUMU === "Onaylandı" || s.SIPARIS_DURUMU === "Beklemede") && (
              <Button color="success" size="sm" onClick={() => openShipModal(s)}>
                <i className="bx bx-package me-1" />Kargoya Ver
              </Button>
            )}
          </div>
        );
      },
    },
  ], []);

  const tabItems = [
    { key: "tumu", label: "Tümü", count: siparisler.length },
    { key: "OdemeBekliyor", label: "Ödeme Bekliyor", count: statusCounts["OdemeBekliyor"] || 0 },
    { key: "Beklemede", label: "Beklemede", count: statusCounts["Beklemede"] || 0 },
    { key: "Onaylandı", label: "Onaylandı", count: statusCounts["Onaylandı"] || 0 },
    { key: "Kargoya Verildi", label: "Kargoya Verildi", count: statusCounts["Kargoya Verildi"] || 0 },
    { key: "Teslim Edildi", label: "Teslim Edildi", count: statusCounts["Teslim Edildi"] || 0 },
    { key: "İptal Edildi", label: "İptal Edildi", count: statusCounts["İptal Edildi"] || 0 },
  ];

  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="HarmanaGEL" breadcrumbItem="Sipariş Yönetimi" />

          {successMsg && (
            <Alert color="success" className="d-flex align-items-center justify-content-between">
              <span><i className="bx bx-check-circle me-2" />{successMsg}</span>
              {lastCreatedShipment && (
                <Button color="primary" size="sm" onClick={handlePrintLabel}>
                  <i className="bx bx-printer me-1" />Etiket İndir
                </Button>
              )}
            </Alert>
          )}
          {error && <Alert color="danger" toggle={() => setError(null)}>{error}</Alert>}

          {/* Özet Kartlar */}
          <Row className="mb-3">
            {[
              { label: "Toplam Sipariş", count: siparisler.length, color: "primary", icon: "bx-cart" },
              { label: "Beklemede", count: statusCounts["Beklemede"] || 0, color: "warning", icon: "bx-time" },
              { label: "Teslim Edildi", count: statusCounts["Teslim Edildi"] || 0, color: "success", icon: "bx-check-circle" },
              { label: "İptal Edildi", count: statusCounts["İptal Edildi"] || 0, color: "danger", icon: "bx-x-circle" },
            ].map((item, idx) => (
              <Col md={3} key={idx}>
                <Card className="mini-stats-wid">
                  <CardBody>
                    <div className="d-flex">
                      <div className="flex-grow-1">
                        <p className="text-muted fw-medium">{item.label}</p>
                        <h4 className="mb-0">{item.count}</h4>
                      </div>
                      <div className={`avatar-sm align-self-center mini-stat-icon rounded-circle bg-${item.color}`}>
                        <span className="avatar-title"><i className={`bx ${item.icon} font-size-24`} /></span>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            ))}
          </Row>

          <Row>
            <Col lg={12}>
              <Card>
                <CardBody className="pt-0 pb-0 border-bottom">
                  <Nav tabs className="nav-tabs-custom">
                    {tabItems.map(tab => (
                      <NavItem key={tab.key}>
                        <NavLink
                          className={classnames({ active: activeTab === tab.key })}
                          onClick={() => setActiveTab(tab.key)}
                          style={{ cursor: "pointer" }}
                        >
                          {tab.label}
                          {tab.count > 0 && (
                            <Badge color="primary" pill className="ms-1">{tab.count}</Badge>
                          )}
                        </NavLink>
                      </NavItem>
                    ))}
                  </Nav>
                </CardBody>
                <CardBody>
                  <div className="d-flex justify-content-end mb-2 gap-2">
                    <Button
                      color="secondary"
                      size="sm"
                      outline
                      onClick={() =>
                        downloadObjectsCsv(
                          `siparisler-${new Date().toISOString().slice(0, 10)}.csv`,
                          filteredSiparisler,
                          [
                            { key: "ID", label: "ID" },
                            {
                              label: "Müşteri",
                              get: (s) => s.KULLANICILAR?.AD_SOYAD || "",
                            },
                            { key: "TOPLAM_TUTAR", label: "Tutar" },
                            {
                              label: "Durum",
                              get: (s) => durumLabel(s.SIPARIS_DURUMU),
                            },
                            {
                              label: "Tarih",
                              get: (s) =>
                                s.SIPARIS_TARIHI
                                  ? new Date(s.SIPARIS_TARIHI).toLocaleString("tr-TR")
                                  : "",
                            },
                          ]
                        )
                      }
                      disabled={!filteredSiparisler.length}
                    >
                      <i className="bx bx-export me-1" />
                      CSV
                    </Button>
                    <Button color="primary" size="sm" onClick={fetchSiparisler} disabled={isLoading}>
                      <i className="bx bx-refresh me-1" />
                      Yenile
                    </Button>
                  </div>
                  {isLoading ? (
                    <div className="text-center py-5">
                      <Spinner color="primary" />
                    </div>
                  ) : (
                    <TableContainer
                      columns={columns}
                      data={filteredSiparisler}
                      isGlobalFilter={true}
                      SearchPlaceholder="Sipariş ara..."
                      isPagination={true}
                      isCustomPageSize={true}
                      tableClass="table-bordered align-middle nowrap w-100 mb-0"
                      theadClass="table-light"
                      paginationWrapper="dataTables_paginate paging_simple_numbers pagination-rounded"
                    />
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>

      {/* Kargo Oluştur Modal */}
      <Modal isOpen={shipModal} toggle={() => setShipModal(false)} size="lg" centered>
        <ModalHeader toggle={() => setShipModal(false)}>
          <i className="bx bxs-package me-2 text-success" />
          Kargoya Ver — Sipariş #{selectedSiparis?.ID}
        </ModalHeader>
        <ModalBody>
          {/* Adres yükleniyor */}
          {addressLoading && (
            <div className="alert alert-info py-2 d-flex align-items-center gap-2">
              <Spinner size="sm" />
              <span>Teslimat adresi yükleniyor...</span>
            </div>
          )}

          {/* Provider Seçimi */}
          <div className="d-flex gap-2 mb-4">
            <Button
              color={shipProvider === "ke" ? "primary" : "outline-secondary"}
              size="sm"
              onClick={() => handleProviderChange("ke")}
            >
              <i className="bx bx-truck me-1" />KargoEntegratör
              <Badge color="success" className="ms-1">Yurt İçi</Badge>
            </Button>
            <Button
              color={shipProvider === "se" ? "primary" : "outline-secondary"}
              size="sm"
              onClick={() => handleProviderChange("se")}
            >
              <i className="bx bx-globe me-1" />ShipEntegra
              <Badge color="info" className="ms-1">Yurt Dışı</Badge>
            </Button>
          </div>

          {/* KargoEntegratör Formu */}
          {shipProvider === "ke" && (
            <>
              {keLoading ? (
                <div className="text-center py-3"><Spinner size="sm" /> Yükleniyor...</div>
              ) : keError ? (
                <div className="alert alert-danger">
                  <strong>KargoEntegratör bağlantı hatası:</strong>
                  <div className="small mt-1" style={{ wordBreak: "break-all" }}>{keError}</div>
                  <Button color="danger" size="sm" outline className="mt-2" onClick={() => { setKeError(null); setKeIntegrations([]); setKeWarehouses([]); fetchKEData(); }}>
                    <i className="bx bx-refresh me-1" />Tekrar Dene
                  </Button>
                </div>
              ) : (
                <Row>
                  <Col md={6}>
                    <h6 className="mb-3 text-muted">Kargo Ayarları</h6>
                    <FormGroup>
                      <Label>Kargo Firması *</Label>
                      <Input type="select" value={keForm.cargo_integration_id}
                        onChange={(e) => setKeForm({ ...keForm, cargo_integration_id: e.target.value })}>
                        <option value="">-- Kargo Seçin --</option>
                        {keIntegrations.map((i) => (
                          <option key={i.id} value={i.id}>
                            {i.name || i.cargo_company?.title || `Entegrasyon #${i.id}`}
                          </option>
                        ))}
                      </Input>
                    </FormGroup>
                    <FormGroup>
                      <Label>Depo *</Label>
                      <Input type="select" value={keForm.warehouse_id}
                        onChange={(e) => setKeForm({ ...keForm, warehouse_id: e.target.value })}>
                        <option value="">-- Depo Seçin --</option>
                        {keWarehouses.map((w) => (
                          <option key={w.id} value={w.id}>
                            {w.name}{w.is_default ? " (Varsayılan)" : ""}
                          </option>
                        ))}
                      </Input>
                    </FormGroup>
                    <FormGroup>
                      <Label>Ödeyen Taraf</Label>
                      <Input type="select" value={keForm.payor_type}
                        onChange={(e) => setKeForm({ ...keForm, payor_type: e.target.value })}>
                        <option value="sender">Gönderici Öder</option>
                        <option value="reciver">Alıcı Öder</option>
                      </Input>
                    </FormGroup>
                    <Row>
                      <Col>
                        <FormGroup>
                          <Label>Ağırlık (kg)</Label>
                          <Input type="number" min="0.1" step="0.1" value={keForm.kg}
                            onChange={(e) => setKeForm({ ...keForm, kg: e.target.value })} />
                        </FormGroup>
                      </Col>
                      <Col>
                        <FormGroup>
                          <Label>Desi</Label>
                          <Input type="number" min="1" value={keForm.desi}
                            onChange={(e) => setKeForm({ ...keForm, desi: e.target.value })} />
                        </FormGroup>
                      </Col>
                    </Row>
                    <FormGroup>
                      <Label>Not</Label>
                      <Input value={keForm.note}
                        onChange={(e) => setKeForm({ ...keForm, note: e.target.value })} />
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <h6 className="mb-3 text-muted">Alıcı Bilgileri</h6>
                    <Row>
                      <Col>
                        <FormGroup>
                          <Label>Ad *</Label>
                          <Input value={keForm.name}
                            onChange={(e) => setKeForm({ ...keForm, name: e.target.value })} />
                        </FormGroup>
                      </Col>
                      <Col>
                        <FormGroup>
                          <Label>Soyad *</Label>
                          <Input value={keForm.surname}
                            onChange={(e) => setKeForm({ ...keForm, surname: e.target.value })} />
                        </FormGroup>
                      </Col>
                    </Row>
                    <FormGroup>
                      <Label>Telefon *</Label>
                      <Input value={keForm.phone}
                        onChange={(e) => setKeForm({ ...keForm, phone: e.target.value })}
                        placeholder="5XXXXXXXXX" />
                    </FormGroup>
                    <FormGroup>
                      <Label>Şehir *</Label>
                      <Input value={keForm.city}
                        onChange={(e) => setKeForm({ ...keForm, city: e.target.value })}
                        placeholder="ISTANBUL" />
                    </FormGroup>
                    <FormGroup>
                      <Label>İlçe</Label>
                      <Input value={keForm.district}
                        onChange={(e) => setKeForm({ ...keForm, district: e.target.value })}
                        placeholder="KADIKÖY" />
                    </FormGroup>
                    <FormGroup>
                      <Label>Posta Kodu</Label>
                      <Input value={keForm.postcode}
                        onChange={(e) => setKeForm({ ...keForm, postcode: e.target.value })}
                        placeholder="34000" />
                    </FormGroup>
                    <FormGroup>
                      <Label>Adres *</Label>
                      <Input type="textarea" rows={2} value={keForm.address}
                        onChange={(e) => setKeForm({ ...keForm, address: e.target.value })}
                        placeholder="Açık adres..." />
                    </FormGroup>
                  </Col>
                </Row>
              )}
            </>
          )}

          {/* ShipEntegra Formu */}
          {shipProvider === "se" && (
            <Row>
              <Col md={6}>
                <h6 className="mb-3 text-muted">Kargo Ayarları</h6>
                <FormGroup>
                  <Label>Kargo Firması *</Label>
                  {carriersLoading ? (
                    <div className="text-center py-2"><Spinner size="sm" /> Yükleniyor...</div>
                  ) : (
                    <Input type="select" value={shipForm.carrierId}
                      onChange={(e) => setShipForm({ ...shipForm, carrierId: e.target.value })}>
                      <option value="">-- Seçin --</option>
                      {carriers.map((c, i) => (
                        <option key={i} value={c.id || c.Id || c.carrierId}>
                          {c.name || c.Name || c.carrierName || `Firma ${i + 1}`}
                        </option>
                      ))}
                    </Input>
                  )}
                </FormGroup>
                <FormGroup>
                  <Label>Ödeme Tipi</Label>
                  <Input type="select" value={shipForm.paymentType}
                    onChange={(e) => setShipForm({ ...shipForm, paymentType: e.target.value })}>
                    <option value="0">Gönderici Öder</option>
                    <option value="1">Kapıda Ödeme</option>
                  </Input>
                </FormGroup>
                <Row>
                  <Col>
                    <FormGroup>
                      <Label>Ağırlık (kg)</Label>
                      <Input type="number" min="0.1" step="0.1" value={shipForm.weight}
                        onChange={(e) => setShipForm({ ...shipForm, weight: e.target.value })} />
                    </FormGroup>
                  </Col>
                  <Col>
                    <FormGroup>
                      <Label>Desi</Label>
                      <Input type="number" min="1" value={shipForm.desi}
                        onChange={(e) => setShipForm({ ...shipForm, desi: e.target.value })} />
                    </FormGroup>
                  </Col>
                </Row>
                <FormGroup>
                  <Label>Açıklama</Label>
                  <Input value={shipForm.description}
                    onChange={(e) => setShipForm({ ...shipForm, description: e.target.value })} />
                </FormGroup>
              </Col>
              <Col md={6}>
                <h6 className="mb-3 text-muted">Alıcı Bilgileri</h6>
                <FormGroup>
                  <Label>Ad Soyad *</Label>
                  <Input value={shipForm.receiverName}
                    onChange={(e) => setShipForm({ ...shipForm, receiverName: e.target.value })} />
                </FormGroup>
                <FormGroup>
                  <Label>Telefon *</Label>
                  <Input value={shipForm.receiverPhone}
                    onChange={(e) => setShipForm({ ...shipForm, receiverPhone: e.target.value })} />
                </FormGroup>
                <FormGroup>
                  <Label>Şehir *</Label>
                  <Input value={shipForm.receiverCity}
                    onChange={(e) => setShipForm({ ...shipForm, receiverCity: e.target.value })}
                    placeholder="İstanbul" />
                </FormGroup>
                <FormGroup>
                  <Label>İlçe</Label>
                  <Input value={shipForm.receiverDistrict}
                    onChange={(e) => setShipForm({ ...shipForm, receiverDistrict: e.target.value })}
                    placeholder="Kadıköy" />
                </FormGroup>
                <FormGroup>
                  <Label>Posta Kodu</Label>
                  <Input value={shipForm.receiverPostalCode}
                    onChange={(e) => setShipForm({ ...shipForm, receiverPostalCode: e.target.value })}
                    placeholder="34000" />
                </FormGroup>
                <FormGroup>
                  <Label>Adres *</Label>
                  <Input type="textarea" rows={2} value={shipForm.receiverAddress}
                    onChange={(e) => setShipForm({ ...shipForm, receiverAddress: e.target.value })}
                    placeholder="Açık adres..." />
                </FormGroup>
              </Col>
            </Row>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setShipModal(false)} disabled={shipLoading}>İptal</Button>
          <Button color="success" onClick={handleCreateShipment} disabled={shipLoading}>
            {shipLoading ? <><Spinner size="sm" className="me-1" />Gönderiliyor...</> : <><i className="bx bx-package me-1" />Kargoyu Oluştur</>}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Sipariş Detay & Durum Güncelle Modal */}
      <Modal isOpen={detailModal} toggle={() => setDetailModal(false)} size="lg" centered>
        <ModalHeader toggle={() => setDetailModal(false)}>
          Sipariş #{selectedSiparis?.ID}
        </ModalHeader>
        <ModalBody>
          {selectedSiparis && (
            <>
              <Row className="mb-3">
                <Col md={6}>
                  <table className="table table-sm table-bordered">
                    <tbody>
                      <tr><th>Müşteri</th><td>{selectedSiparis.KULLANICILAR?.AD_SOYAD || `ID: ${selectedSiparis.KULLANICI_ID}`}</td></tr>
                      <tr><th>Toplam Tutar</th><td className="fw-bold text-success">{parseFloat(selectedSiparis.TOPLAM_TUTAR || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2 })} ₺</td></tr>
                      <tr><th>Sipariş Tarihi</th><td>{selectedSiparis.SIPARIS_TARIHI ? new Date(selectedSiparis.SIPARIS_TARIHI).toLocaleDateString("tr-TR") : "-"}</td></tr>
                      <tr><th>Kargo Firması</th><td>{selectedSiparis.KARGO_FIRMASI || "-"}</td></tr>
                      <tr><th>Kargo No</th><td>{selectedSiparis.KARGO_NO || "-"}</td></tr>
                      <tr><th>Sipariş Notu</th><td>{selectedSiparis.SIPARIS_NOTU || "-"}</td></tr>
                    </tbody>
                  </table>
                </Col>
                <Col md={6}>
                  <div className="mb-3">
                    <label className="form-label fw-medium">Mevcut Durum</label>
                    <div>
                      <Badge color={DURUM_RENKLERI[selectedSiparis.SIPARIS_DURUMU] || "secondary"} className="p-2 fs-6">
                        {selectedSiparis.SIPARIS_DURUMU}
                      </Badge>
                    </div>
                  </div>
                  <div>
                    <label className="form-label fw-medium">Durumu Güncelle</label>
                    <Input
                      type="select"
                      value={newStatus}
                      onChange={(e) => setNewStatus(e.target.value)}
                    >
                      <option value="OdemeBekliyor">Ödeme Bekliyor</option>
                      <option value="Beklemede">Beklemede</option>
                      <option value="Onaylandı">Onaylandı</option>
                      <option value="Kargoya Verildi">Kargoya Verildi</option>
                      <option value="Teslim Edildi">Teslim Edildi</option>
                      <option value="İptal Edildi">İptal Edildi</option>
                      <option value="İade Edildi">İade Edildi</option>
                    </Input>
                  </div>
                </Col>
              </Row>

              {selectedSiparis.IPTAL_ACIKLAMASI && (
                <div className="alert alert-danger">
                  <strong>İptal Açıklaması:</strong> {selectedSiparis.IPTAL_ACIKLAMASI}
                </div>
              )}

              {Array.isArray(selectedSiparis.SIPARIS_DETAY) && selectedSiparis.SIPARIS_DETAY.length > 0 && (
                <>
                  <h6 className="mt-3 mb-2">Sipariş Kalemleri</h6>
                  <table className="table table-sm table-striped">
                    <thead className="table-light">
                      <tr>
                        <th>ID</th>
                        <th>Ürün ID</th>
                        <th>Adet</th>
                        <th>Birim Fiyat</th>
                        <th>Toplam</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedSiparis.SIPARIS_DETAY.map((d, i) => (
                        <tr key={i}>
                          <td>#{d.ID}</td>
                          <td>{d.ILANLAR_ID || d.URUN_ID || "-"}</td>
                          <td>{d.ADET || 1}</td>
                          <td>{parseFloat(d.BIRIM_FIYAT || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2 })} ₺</td>
                          <td className="fw-medium">{parseFloat(d.TOPLAM || d.BIRIM_FIYAT || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2 })} ₺</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setDetailModal(false)}>Kapat</Button>
          <Button color="primary" onClick={handleUpdateStatus}>
            Durumu Güncelle
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default SiparisYonetimi;
